import React, { useCallback, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import Box from '@material-ui/core/Box'
import CalendarTodayOutlinedIcon from 'components/material-symbol/icons/CalendarToday'
import EmptyPageState, { TonalButton } from 'components/empty-page-state'
import { BOOKINGS_QUERY } from './query'
import Error from 'components/error'
import { DateTime } from 'luxon'
import { PGA_COM_HOST } from 'env'
import SocialShareDialog from 'components/social-share-dialog'
import { onReschedule, onPayment, onCancellation } from 'lib/utils'
import { RefetchContext } from 'lib/utils/refetchContext'
import {
  bookingsForTodaySchedule,
  bookingsUpcoming,
} from 'pages/pga-coach/bookings/schedule/bookings-list-helpers'
import TodaysSchedule from './todays-schedule'
import ManageBusiness from './manage-business'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import GrowthPerformance from './growth-performance'
import MarketplaceListingBanner from './marketplace-listing-banner'
import VirtualCoachingTaskBanner from './virtual-coaching-task-banner'
import useStyles from './styles'

const UPCOMING_LESSONS_LIMIT = 4

const DefaultDashboard = ({
  hasGroupEventsOrLessonTypes,
  slug,
  marketplaceListingReasons = [],
  upgradeFlow,
}) => {
  const classes = useStyles()
  const [isCoachBenchmarksEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_COACH_BENCHMARKS,
  ])
  const shareUrl = slug ? `${PGA_COM_HOST}/coach/${slug}` : null

  const todayVariables = useMemo(
    () => ({
      startDateTime: {
        from: DateTime.local()
          .startOf('day')
          .toISO(),
        to: DateTime.local()
          .endOf('day')
          .toISO(),
      },
      orderBy: [{ field: 'START_DATE_TIME', direction: 'ASC' }],
    }),
    [],
  )

  const upcomingVariables = useMemo(
    () => ({
      startDateTime: {
        from: DateTime.local().toISO(),
        to: null,
      },
      orderBy: [{ field: 'START_DATE_TIME', direction: 'ASC' }],
      limit: UPCOMING_LESSONS_LIMIT,
    }),
    [],
  )

  const {
    data: todayData,
    loading: todayLoading,
    error: todayError,
    refetch: refetchTodaySchedule,
  } = useQuery(BOOKINGS_QUERY, { variables: todayVariables })

  const {
    data: upcomingData,
    loading: upcomingLoading,
    error: upcomingError,
    refetch: refetchUpcomingLessons,
  } = useQuery(BOOKINGS_QUERY, { variables: upcomingVariables })

  const refreshBookings = useCallback(
    () => Promise.all([refetchTodaySchedule(), refetchUpcomingLessons()]),
    [refetchTodaySchedule, refetchUpcomingLessons],
  )

  if (todayError || upcomingError) return <Error />

  const schedule = bookingsForTodaySchedule(
    todayData?.currentUser?.schedule || [],
  )
  const upcomingLessons = bookingsUpcoming(
    upcomingData?.currentUser?.schedule || [],
    { limit: UPCOMING_LESSONS_LIMIT },
  )
  const isScheduleLoading =
    (todayLoading && !todayData) || (upcomingLoading && !upcomingData)

  return (
    <Box className={classes.mainContent}>
      <MarketplaceListingBanner
        reasons={marketplaceListingReasons}
        upgradeFlow={upgradeFlow}
      />
      <VirtualCoachingTaskBanner />
      {hasGroupEventsOrLessonTypes ? (
        <RefetchContext.Provider value={refreshBookings}>
          <TodaysSchedule
            schedule={schedule}
            loading={isScheduleLoading}
            shareUrl={shareUrl}
            onReschedule={() => onReschedule(refreshBookings)}
            onPayment={() => onPayment(refreshBookings)}
            onCancellation={() => onCancellation(refreshBookings)}
          />
          <ManageBusiness
            upcomingLessons={upcomingLessons}
            lessonsLoading={isScheduleLoading}
          />
        </RefetchContext.Provider>
      ) : (
        <EmptyPageState
          IconComp={() => <CalendarTodayOutlinedIcon />}
          title="Your Tee Sheet is Wide Open!"
          caption="Your upcoming lessons and events will display here. Share your booking link with students to start scheduling lessons using PGA Coach!"
          action={
            shareUrl ? (
              <SocialShareDialog
                trigger={<TonalButton>Share Booking Link</TonalButton>}
                shareUrl={shareUrl}
                shareTitle="Booking Link"
                showBookingUrl
                att="booking-link"
              />
            ) : null
          }
        />
      )}
      {isCoachBenchmarksEnabled ? <GrowthPerformance /> : null}
    </Box>
  )
}

export default DefaultDashboard
